'use client';

import Image from 'next/image';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { LandingNav, LandingNavLink } from '../../_lib/schema/landing-config.schema';
import { pickLocalized } from './landing-context';
import styles from './floating-site-chrome.module.css';

type FloatingChromeProps = { nav: LandingNav; locale: string; logoUrl?: string | null };

function Brand({ nav, logoUrl, footer = false }: { nav: LandingNav; logoUrl?: string | null; footer?: boolean }) {
  return (
    <span className={styles.brand} data-footer={footer}>
      {logoUrl ? <Image src={logoUrl} alt={nav.logo?.alt || nav.brand?.wordmark || ''} width={220} height={72} unoptimized className={styles.logo} /> : <span className={styles.wordmark}>{nav.brand?.wordmark || nav.logo?.alt}</span>}
      {footer && nav.brand?.tagline ? <span className={styles.caption}>{nav.brand.tagline}</span> : null}
    </span>
  );
}

function NavigationLinks({ nav, locale, onNavigate, footer = false }: FloatingChromeProps & { onNavigate?: () => void; footer?: boolean }) {
  const links: LandingNavLink[] = footer && nav.footerLinks ? nav.footerLinks : nav.links;
  return (
    <>
      {links.map((link, index) => link.children?.length ? (
        <details className={styles.linkGroup} key={`${link.href}-${index}`} onKeyDown={(event) => {
          if (event.key !== 'Escape' || !event.currentTarget.open) return;
          event.stopPropagation();
          event.currentTarget.open = false;
          event.currentTarget.querySelector('summary')?.focus();
        }}>
          <summary>{pickLocalized(link.label, link.label_ar, locale)}</summary>
          <div className={styles.groupLinks}>
            {[link, ...link.children].map((child, childIndex) => <a key={`${child.href}-${childIndex}`} href={child.href} onClick={(event) => {
              event.currentTarget.closest('details')?.removeAttribute('open');
              onNavigate?.();
            }}>{pickLocalized(child.label, child.label_ar, locale)}</a>)}
          </div>
        </details>
      ) : (
        <a key={`${link.href}-${index}`} href={link.href} onClick={onNavigate}>
          {pickLocalized(link.label, link.label_ar, locale)}
        </a>
      ))}
      {nav.primaryAction && !(footer && nav.footerLinks) ? <a href={nav.primaryAction.href} onClick={onNavigate}>{pickLocalized(nav.primaryAction.label, nav.primaryAction.label_ar, locale)}</a> : null}
    </>
  );
}

export function FloatingSiteHeader({ nav, locale, logoUrl, accountControl, languageControl, memberNavigation }: FloatingChromeProps & {
  accountControl?: ReactNode;
  languageControl?: ReactNode;
  memberNavigation?: ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  const header = useRef<HTMLElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const [state, setState] = useState({ visible: true, menuOpen: false });

  useEffect(() => {
    const doc = header.current?.ownerDocument;
    const view = doc?.defaultView;
    if (!doc || !view) return;
    let lastY = view.scrollY;
    let timer: number | undefined;
    const onScroll = () => {
      const y = view.scrollY;
      const visible = y <= 20 || y < lastY;
      setState((current) => ({ ...current, visible }));
      lastY = y;
      view.clearTimeout(timer);
      timer = view.setTimeout(() => setState((current) => ({ ...current, visible: true })), 400);
    };
    const outside = (event: PointerEvent) => {
      if (!event.composedPath().includes(header.current as EventTarget)) {
        setState((current) => ({ ...current, menuOpen: false }));
      }
    };
    view.addEventListener('scroll', onScroll, { passive: true });
    doc.addEventListener('pointerdown', outside);
    return () => {
      view.clearTimeout(timer);
      view.removeEventListener('scroll', onScroll);
      doc.removeEventListener('pointerdown', outside);
    };
  }, []);

  const close = () => setState((current) => ({ ...current, menuOpen: false }));

  return (
    <header ref={header} className={styles.header} data-visible={state.visible || state.menuOpen} data-test="floating-site-header" onKeyDown={(event) => {
      if (event.key !== 'Escape' || !state.menuOpen) return;
      event.stopPropagation();
      close();
      toggle.current?.focus();
    }} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) close();
    }}>
      <div className={styles.pill}>
        <a href="/" className={styles.brandLink} onClick={close}><Brand nav={nav} logoUrl={logoUrl} /></a>
        <span className={styles.divider} aria-hidden="true" />
        <nav className={styles.desktopLinks} aria-label={t('marketing:sportsChrome.navigation')}>
          <NavigationLinks nav={nav} locale={locale} />
        </nav>
        {languageControl}
        {accountControl}
        <button ref={toggle} className={styles.toggle} type="button" aria-label={t('marketing:sportsChrome.menu')} aria-expanded={state.menuOpen} aria-controls={id} onClick={() => setState((current) => ({ ...current, menuOpen: !current.menuOpen }))}>
          <Menu size={18} aria-hidden="true" />
        </button>
      </div>
      <nav id={id} className={styles.mobileLinks} hidden={!state.menuOpen} aria-label={t('marketing:sportsChrome.navigation')}>
        <NavigationLinks nav={nav} locale={locale} onNavigate={close} />
      </nav>
      {memberNavigation}
    </header>
  );
}

export function FloatingSiteFooter({ nav, locale, logoUrl, legalControls }: FloatingChromeProps & { legalControls?: ReactNode }) {
  const { t } = useTranslation();
  const text = pickLocalized(nav.brand?.footerText, nav.brand?.footerText_ar, locale);
  const motto = pickLocalized(nav.brand?.motto, nav.brand?.motto_ar, locale) ?? nav.brand?.tagline;
  const copyright = pickLocalized(nav.brand?.copyright, nav.brand?.copyright_ar, locale);
  return (
    <footer className={styles.footer} data-test="floating-site-footer">
      <div className={styles.footerInner}>
        <div className={styles.footerRow}>
          <div><Brand nav={nav} logoUrl={logoUrl} footer />{text ? <p className={styles.footerText}>{text}</p> : null}</div>
          <nav className={styles.footerLinks} aria-label={t('marketing:sportsChrome.footerNavigation')}><NavigationLinks nav={nav} locale={locale} footer /></nav>
        </div>
        <div className={styles.footerBottom}>
          <span>© {new Date().getFullYear()} {copyright || <span className={styles.inlineWordmark}>{nav.brand?.wordmark || nav.logo?.alt}</span>}</span>
          {motto ? <span className={styles.footerTagline}>{motto}</span> : null}
        </div>
        {legalControls}
      </div>
    </footer>
  );
}
